/**
 * "How Fomo-Fi works" (doc 3 "The walkthrough overlay", E1-E16; Paul 2026-10-28):
 * the rules behind the overlay, kept out of the component so they're testable.
 *
 * Where the step is remembered:
 * - Signed in: on the account (`profile.walkthrough_last_step`, doc 3 G2), so resume
 *   works after a reload and on another device (E11).
 * - Signed out (the splash page, sign-up before an account exists): there is no
 *   account yet, so this browser's storage holds it. It's a cosmetic cursor; losing
 *   it only means the walkthrough opens at its page's step again. Conservative
 *   default, listed for Paul.
 */
import { WALKTHROUGH_STEP_COUNT, clampStep } from "@fomofi/shared";

// The pure rules are shared with mobile (shared/utils/walkthroughRules.ts); web keeps
// the signed-out storage below.
export { canGoBack, clampStep, closeOutcome, closeTourOutcome, openingStep } from "@fomofi/shared";

export type WalkthroughStepNumber = number;

const ANON_KEY = "fomo-fi:walkthrough-last-step";

/** Signed-out resume: read this browser's saved step. Storage can throw (private mode). */
export function readAnonStep(): number | null {
  try {
    const raw = localStorage.getItem(ANON_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1 && n <= WALKTHROUGH_STEP_COUNT ? n : null;
  } catch {
    return null;
  }
}

export function writeAnonStep(step: number | null): void {
  try {
    if (step == null) localStorage.removeItem(ANON_KEY);
    else localStorage.setItem(ANON_KEY, String(clampStep(step)));
  } catch {
    // Storage disabled: resume falls back to the page's step. Nothing breaks.
  }
}
